import { app, type ResumeContext } from '@microsoft/teams-js';
import { focusManager } from '@tanstack/react-query';

import {
  notifyAppBackground,
  notifyAppForeground,
} from '@/lib/app-presence.js';
import { isRunningInTeams } from '@/lib/teams-env.js';
import { setTeamsAppSuspended } from '@/lib/teams-suspension.js';
import { withTimeout } from '@/lib/with-timeout.js';
import { logger } from '@/logger.js';

/** The SDK shares one initialisation with the sign-in path; this only bounds a host that never answers. */
const LIFECYCLE_INIT_TIMEOUT_MS = 30_000;

/** Teams waits at most 30 s for the suspend handler; unsaved work gets a small, fixed slice of it. */
const SUSPEND_FLUSH_BUDGET_MS = 2_000;

/** How long resume listeners may run before Teams is told the tab is ready. */
const RESUME_LISTENERS_BUDGET_MS = 3_000;

/** Called after Teams shows a suspended tab again, with the tab Teams asked for. */
export type TeamsResumeListener = (
  context: ResumeContext,
) => Promise<void> | void;

const resumeListeners = new Set<TeamsResumeListener>();
let registration: Promise<void> | null = null;

/**
 * Opts this app into Teams app suspension: registering both lifecycle handlers keeps the tab's
 * iframe alive, hidden, when the user switches to a Teams chat, instead of rebuilding it on return.
 * Call it early in boot: Teams caches the tab only when both handlers exist before the user first
 * leaves it. Runs once and only inside Teams (outside it `app.initialize()` never settles); any SDK
 * failure is logged and the app simply keeps today's reload behaviour.
 */
export function registerTeamsLifecycle(): Promise<void> {
  if (registration === null) {
    registration = isRunningInTeams() ? registerHandlers() : Promise.resolve();
  }
  return registration;
}

/**
 * Subscribes to Teams resuming the tab. Listeners run before Teams is told the tab is ready, so a
 * deep link or a data refresh can land first.
 *
 * @returns a function that removes the listener.
 */
export function onTeamsResume(listener: TeamsResumeListener): () => void {
  resumeListeners.add(listener);
  return () => {
    resumeListeners.delete(listener);
  };
}

async function registerHandlers(): Promise<void> {
  try {
    await withTimeout(
      app.initialize(),
      LIFECYCLE_INIT_TIMEOUT_MS,
      'Teams app.initialize',
    );
    app.lifecycle.registerBeforeSuspendOrTerminateHandler(prepareForSuspend);
    app.lifecycle.registerOnResumeHandler((context) => {
      void resumeFromSuspend(context);
    });
  } catch (error: unknown) {
    logger.debug('teams-lifecycle: suspension not available', error);
  }
}

/**
 * Teams is about to hide the tab. Unsaved work is flushed while renewal is still allowed, then the
 * tab is marked suspended and react-query stops its interval refetches. It never rejects: Teams waits
 * for this promise and terminates an app that does not answer.
 */
async function prepareForSuspend(): Promise<void> {
  try {
    await settleWithin(
      notifyAppBackground('teams-suspend'),
      SUSPEND_FLUSH_BUDGET_MS,
    );
    setTeamsAppSuspended(true);
    focusManager.setFocused(false);
  } catch (error: unknown) {
    logger.debug('teams-lifecycle: suspend preparation failed', error);
  }
}

/** Teams shows the tab again: wake it, let listeners catch up, then report the tab as ready. */
async function resumeFromSuspend(context: ResumeContext): Promise<void> {
  setTeamsAppSuspended(false);
  focusManager.setFocused(undefined);
  await settleWithin(runResumeListeners(context), RESUME_LISTENERS_BUDGET_MS);
  notifyAppForeground();
  await reportResumeHandled();
}

async function runResumeListeners(context: ResumeContext): Promise<void> {
  await Promise.allSettled(
    [...resumeListeners].map(async (listener) => listener(context)),
  );
}

async function reportResumeHandled(): Promise<void> {
  try {
    await app.notifySuccess();
  } catch (error: unknown) {
    logger.debug('teams-lifecycle: notifySuccess failed', error);
  }
}

/** Resolves when `work` settles or when the budget runs out, whichever comes first; never rejects. */
function settleWithin(work: Promise<unknown>, budgetMs: number): Promise<void> {
  return new Promise<void>((resolve) => {
    const timer = setTimeout(resolve, budgetMs);
    void work
      .catch(() => undefined)
      .finally(() => {
        clearTimeout(timer);
        resolve();
      });
  });
}

/** Test-only: forgets the registration and every resume listener. */
export function _resetTeamsLifecycleForTests(): void {
  registration = null;
  resumeListeners.clear();
}
